<template>
  <div>
    <div class="rounded-lg border border-base-content/15 bg-base-200/30 p-1.5 mb-3">
      <div class="flex w-full gap-2">
        <button
          class="btn btn-sm flex-1 rounded-md border-base-300/60"
          :class="tab === 'players' ? 'btn-primary' : 'btn-ghost'"
          type="button"
          @click="$emit('tab-change', 'players')"
        >
          Players
        </button>
        <button
          class="btn btn-sm flex-1 rounded-md border-base-300/60"
          :class="tab === 'stocks' ? 'btn-primary' : 'btn-ghost'"
          type="button"
          @click="$emit('tab-change', 'stocks')"
        >
          Stocks
        </button>
      </div>
    </div>

    <h2 class="card-title text-base">Leaderboard</h2>
    <div class="border-t border-dashed border-base-content/20 mt-2 mb-3"></div>

    <div v-if="tab === 'players'" class="space-y-2">
      <div
        v-for="(entry, index) in topPlayers"
        :key="entry.display_name"
        class="flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-150 cursor-pointer"
        :class="[
          rankRowBg(index),
          playerHoverClass(entry.display_name),
          focusedPlayerName === entry.display_name ? 'ring-1 ring-primary/60' : '',
        ]"
        @click="$emit('player-click', entry.display_name)"
        @mousemove="$emit('player-hover', $event, entry.display_name)"
        @mouseleave="$emit('player-leave')"
      >
        <div class="w-5 shrink-0 text-center">
          <i v-if="index === 0" class="mdi mdi-trophy text-warning text-base"></i>
          <span v-else class="text-sm font-bold text-base-content/50">{{ entry.rank }}</span>
        </div>
        <div class="flex items-center gap-2 min-w-0 flex-1">
          <span
            class="inline-block w-3 h-3 rounded-[4px] shrink-0"
            :style="{ backgroundColor: CHART_PALETTE[index] ?? '#94a3b8' }"
          ></span>
          <div class="min-w-0">
            <div class="font-semibold text-sm truncate">{{ entry.display_name }}</div>
            <div class="text-xs text-base-content/50">{{ portfolioSummary(entry) }}</div>
          </div>
        </div>
        <div class="text-right font-mono font-semibold text-sm" :class="entry.total_return >= 0 ? 'text-success' : 'text-error'">
          {{ entry.total_return >= 0 ? '+' : '' }}{{ entry.total_return.toFixed(1) }}%
        </div>
      </div>

      <div v-if="missingPlayersCount > 0" class="flex items-center gap-2 px-2 py-1">
        <div class="flex-1 border-t border-dashed border-base-content/20"></div>
        <span class="text-xs text-base-content/30">{{ missingPlayersCount }} more players</span>
        <div class="flex-1 border-t border-dashed border-base-content/20"></div>
      </div>

      <div
        v-if="bottomPlayer"
        class="flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-150 cursor-pointer"
        :class="[
          'bg-base-200/40 border-base-300/50',
          playerHoverClass(bottomPlayer.display_name),
          focusedPlayerName === bottomPlayer.display_name ? 'ring-1 ring-primary/60' : '',
        ]"
        @click="$emit('player-click', bottomPlayer.display_name)"
        @mousemove="$emit('player-hover', $event, bottomPlayer.display_name)"
        @mouseleave="$emit('player-leave')"
      >
        <div class="w-5 shrink-0 text-center">
          <span class="text-sm font-bold text-base-content/50">{{ bottomPlayer.rank }}</span>
        </div>
        <div class="flex items-center gap-2 min-w-0 flex-1">
          <span class="inline-block w-3 h-3 rounded-[4px] shrink-0 bg-base-content/30"></span>
          <div class="min-w-0">
            <div class="font-semibold text-sm truncate">{{ bottomPlayer.display_name }}</div>
            <div class="text-xs text-base-content/50">{{ portfolioSummary(bottomPlayer) }}</div>
          </div>
        </div>
        <div class="text-right font-mono font-semibold text-sm" :class="bottomPlayer.total_return >= 0 ? 'text-success' : 'text-error'">
          {{ bottomPlayer.total_return >= 0 ? '+' : '' }}{{ bottomPlayer.total_return.toFixed(1) }}%
        </div>
      </div>
    </div>

    <div v-if="tab === 'stocks'" class="space-y-2">
      <div
        v-for="(stock, index) in topStocks"
        :key="stock.symbol"
        class="flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-150 cursor-pointer"
        :class="[
          rankRowBg(index),
          stockHoverClass(stock.symbol),
          focusedStockSymbol === stock.symbol ? 'ring-1 ring-primary/60' : '',
        ]"
        @click="$emit('stock-click', stock.symbol)"
        @mouseover="$emit('stock-hover', stock.symbol)"
        @mouseleave="$emit('stock-leave')"
      >
        <div class="w-5 shrink-0 text-center">
          <i v-if="index === 0" class="mdi mdi-trophy text-warning text-base"></i>
          <span v-else class="text-sm font-bold text-base-content/50">{{ index + 1 }}</span>
        </div>
        <div class="flex items-center gap-2 min-w-0 flex-1">
          <span
            class="inline-block w-3 h-3 rounded-[4px] shrink-0"
            :style="{ backgroundColor: CHART_PALETTE[index] ?? '#94a3b8' }"
          ></span>
          <div class="min-w-0">
            <div class="font-semibold text-sm truncate">{{ stock.symbol }}</div>
            <div class="text-xs text-base-content/50">{{ stock.pickCount }} picks • {{ stock.exposure.toFixed(0) }}% exposure</div>
          </div>
        </div>
        <div class="text-right font-mono font-semibold text-sm" :class="stock.returnPct >= 0 ? 'text-success' : 'text-error'">
          {{ stock.returnPct >= 0 ? '+' : '' }}{{ stock.returnPct.toFixed(1) }}%
        </div>
      </div>

      <div v-if="missingStocksCount > 0" class="flex items-center gap-2 px-2 py-1">
        <div class="flex-1 border-t border-dashed border-base-content/20"></div>
        <span class="text-xs text-base-content/30">{{ missingStocksCount }} more stocks</span>
        <div class="flex-1 border-t border-dashed border-base-content/20"></div>
      </div>

      <div
        v-if="bottomStock"
        class="flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-150 cursor-pointer"
        :class="[
          'bg-base-200/40 border-base-300/50',
          stockHoverClass(bottomStock.symbol),
          focusedStockSymbol === bottomStock.symbol ? 'ring-1 ring-primary/60' : '',
        ]"
        @click="$emit('stock-click', bottomStock.symbol)"
        @mouseover="$emit('stock-hover', bottomStock.symbol)"
        @mouseleave="$emit('stock-leave')"
      >
        <div class="w-5 shrink-0 text-center">
          <span class="text-sm font-bold text-base-content/50">{{ universeStocksTotal }}</span>
        </div>
        <div class="flex items-center gap-2 min-w-0 flex-1">
          <span class="inline-block w-3 h-3 rounded-[4px] shrink-0 bg-base-content/30"></span>
          <div class="min-w-0">
            <div class="font-semibold text-sm truncate">{{ bottomStock.symbol }}</div>
            <div class="text-xs text-base-content/50">{{ bottomStock.pickCount }} picks • {{ bottomStock.exposure.toFixed(0) }}% exposure</div>
          </div>
        </div>
        <div class="text-right font-mono font-semibold text-sm" :class="bottomStock.returnPct >= 0 ? 'text-success' : 'text-error'">
          {{ bottomStock.returnPct >= 0 ? '+' : '' }}{{ bottomStock.returnPct.toFixed(1) }}%
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { CHART_PALETTE } from '../lib/apexTheme.js'
import { rankRowBg, portfolioSummary } from '../lib/competitionUtils.js'

const props = defineProps({
  tab: { type: String, required: true },
  topPlayers: { type: Array, required: true },
  bottomPlayer: { type: Object, default: null },
  missingPlayersCount: { type: Number, required: true },
  topStocks: { type: Array, required: true },
  bottomStock: { type: Object, default: null },
  missingStocksCount: { type: Number, required: true },
  universeStocksTotal: { type: Number, required: true },
  hoveredPlayerName: { type: String, default: null },
  focusedPlayerName: { type: String, default: null },
  hoveredStockSymbol: { type: String, default: null },
  focusedStockSymbol: { type: String, default: null },
})

defineEmits(['tab-change', 'player-hover', 'player-leave', 'player-click', 'stock-hover', 'stock-leave', 'stock-click'])

const playerHoverClass = (displayName) =>
  props.hoveredPlayerName === displayName
    ? '!bg-base-content/10 !border-base-content/30'
    : 'hover:!bg-base-content/10 hover:!border-base-content/25'

const stockHoverClass = (symbol) =>
  props.hoveredStockSymbol === symbol
    ? '!bg-base-content/10 !border-base-content/30'
    : 'hover:!bg-base-content/10 hover:!border-base-content/25'
</script>
